import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../../../i18n';
import { dateLocale } from '../../../../lib/localePrefs';
import { Icon } from '../shared/Icon';

const DOW_KEYS = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'] as const;

function iso(y: number, m0: number, d: number): string {
  return `${y}-${String(m0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function monthOf(value: string | null): Date {
  const d = value ? new Date(`${value}T12:00:00`) : new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function shiftMonth(view: Date, delta: number): Date {
  return new Date(view.getFullYear(), view.getMonth() + delta, 1);
}

function todayIso(): string {
  const now = new Date();
  return iso(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * A date range: two labelled date fields (typed or picked with the
 * keyboard) and a month calendar for the mouse. Changes go to the draft of
 * the Filters panel; nothing applies until its Apply.
 */
export function DateRangePicker({
  start,
  end,
  onChange,
  idPrefix,
}: {
  start: string | null;
  end: string | null;
  onChange: (start: string | null, end: string | null) => void;
  idPrefix?: string;
}) {
  const { t } = useTranslation('chrome');
  const auto = useId();
  const base = idPrefix ?? `cx-range-${auto}`;
  const [view, setView] = useState(() => monthOf(end ?? start));
  useEffect(() => {
    setView(monthOf(end ?? start));
    // Follow the range only when it moves to another month from outside.
  }, [start?.slice(0, 7), end?.slice(0, 7)]);

  const pick = (day: string) => {
    if (!start || (start && end)) {
      onChange(day, null);
      return;
    }
    if (day < start) onChange(day, start);
    else onChange(start, day);
  };

  const y = view.getFullYear();
  const m0 = view.getMonth();
  const days = new Date(y, m0 + 1, 0).getDate();
  const firstDow = (new Date(y, m0, 1).getDay() + 6) % 7;
  const cells: Array<{ iso: string; day: number } | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let day = 1; day <= days; day++) cells.push({ iso: iso(y, m0, day), day });
  while (cells.length % 7 !== 0) cells.push(null);
  const today = todayIso();
  const title = view.toLocaleDateString(dateLocale(currentLocale()), { month: 'long', year: 'numeric' });

  return (
    <div className="cx-range">
      <div className="cx-range-fields">
        <label className="cx-field" htmlFor={`${base}-from`}>
          <span>{t('context.panel.period.from')}</span>
          <input
            id={`${base}-from`}
            type="date"
            value={start ?? ''}
            max={end ?? undefined}
            onChange={e => onChange(e.target.value || null, end)}
          />
        </label>
        <label className="cx-field" htmlFor={`${base}-to`}>
          <span>{t('context.panel.period.to')}</span>
          <input
            id={`${base}-to`}
            type="date"
            value={end ?? ''}
            min={start ?? undefined}
            onChange={e => onChange(start, e.target.value || null)}
          />
        </label>
      </div>
      <div className="cx-cal" aria-hidden="false">
        <div className="cx-cal-head">
          <button
            type="button"
            className="cx-cal-nav"
            aria-label={t('period.prevMonth')}
            onClick={() => setView(shiftMonth(view, -1))}
          >
            <Icon name="chevronLeft" size={14} />
          </button>
          <span className="cx-cal-title" aria-live="polite">
            {title}
          </span>
          <button
            type="button"
            className="cx-cal-nav"
            aria-label={t('period.nextMonth')}
            onClick={() => setView(shiftMonth(view, 1))}
          >
            <Icon name="chevronRight" size={14} />
          </button>
        </div>
        <div className="cx-cal-dow" aria-hidden>
          {DOW_KEYS.map(d => (
            <span key={d}>{t(`period.dow.${d}`)}</span>
          ))}
        </div>
        <div className="cx-cal-grid">
          {cells.map((cell, i) => {
            if (!cell) return <span key={`e-${i}`} className="cx-cal-cell is-empty" />;
            const inRange = !!start && !!end && cell.iso >= start && cell.iso <= end;
            const edge = cell.iso === start || cell.iso === end;
            const cls = [
              'cx-cal-cell',
              inRange ? 'is-in' : '',
              edge ? 'is-edge' : '',
              cell.iso === today ? 'is-today' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={cell.iso}
                type="button"
                className={cls}
                tabIndex={-1}
                aria-pressed={edge}
                aria-label={new Date(`${cell.iso}T12:00:00`).toLocaleDateString(dateLocale(currentLocale()), {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
                onClick={() => pick(cell.iso)}
              >
                {cell.day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
